import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { collection, getDocs, query, where, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { resolveMember } from '../utils/resolveMember';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Layout from '../components/Layout';
import { logError } from '../utils/errorLogger';
import { Box, Alert, CircularProgress } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import WaiverDialog from '../components/WaiverDialog';
import { hasValidWaiverAcceptance } from '../utils/waiverGate';

const gold = '#C9A84C';
const goldFaint = 'rgba(201,168,76,0.15)';
const goldMid = 'rgba(201,168,76,0.6)';
const dark = '#0D1E35';

function TrainingPlans() {
  const location = useLocation();
  const previewType = new URLSearchParams(location.search).get('preview'); // 'junior' | 'adult' | null
  const previewMode = previewType === 'junior' || previewType === 'adult';
  const [plans, setPlans] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState('');
  // Waiver gate state. When the selected plan has waiverDocId set and the
  // buyer doesn't have a current acceptance, the dialog opens. After accept,
  // we resume the original checkout call for `pendingPlan` + `pendingAthlete`.
  const [waiverDoc, setWaiverDoc] = useState(null);
  const [waiverDialogOpen, setWaiverDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      // Resolve to the canonical member so linked logins look up (and create)
      // athletes under the real account, not an alias.
      const member = await resolveMember(db, user);
      const muid = member.uid;
      const memail = member.email;

      const planSnap = await getDocs(collection(db, 'plans'));
      const activePlans = planSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.active);
      setPlans(activePlans);

      const savedPlanId = sessionStorage.getItem('selectedPlanId');
      let autoSelectedPlan = null;
      if (savedPlanId) {
        sessionStorage.removeItem('selectedPlanId');
        autoSelectedPlan = activePlans.find(p => p.id === savedPlanId) || null;
        if (autoSelectedPlan) setSelectedPlan(autoSelectedPlan);
      }

      // Linked athletes: check BOTH link fields. Register writes the singular
      // parentUserId, but co-parent linking only adds to the parentUserIds
      // array — querying only the singular field made co-parents' kids
      // invisible here, which triggered the self-stub fallback and misapplied
      // purchases to the parent's own account.
      const [linkedSnap, linkedArrSnap] = await Promise.all([
        getDocs(query(collection(db, 'athletes'), where('parentUserId', '==', muid))),
        getDocs(query(collection(db, 'athletes'), where('parentUserIds', 'array-contains', muid))),
      ]);
      const seenIds = new Set();
      let myAthletes = [];
      [...linkedSnap.docs, ...linkedArrSnap.docs].forEach(d => {
        if (seenIds.has(d.id)) return;
        seenIds.add(d.id);
        myAthletes.push({ id: d.id, ...d.data() });
      });

      const adultSnap = await getDocs(query(collection(db, 'athletes'), where('email', '==', memail)));
      const adultMatch = adultSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(a => a.athleteType?.toLowerCase() === 'adult');

      const existingIds = new Set(myAthletes.map(a => a.id));
      adultMatch.forEach(a => { if (!existingIds.has(a.id)) myAthletes.push(a); });

      const userDocSnap = await getDoc(doc(db, 'users', muid));
      const userData = userDocSnap.exists() ? userDocSnap.data() : {};
      const selfStub = {
        id: muid, athleteId: muid,
        firstName: userData.firstName || user.displayName?.split(' ')[0] || '',
        lastName: userData.lastName || user.displayName?.split(' ').slice(1).join(' ') || '',
        email: memail, phone: userData.phone || '', athleteType: 'adult', sessionBalance: 0,
        _isSelf: true, _memberUid: muid,
      };
      const hasAdultSelf = myAthletes.some(a => a.athleteType?.toLowerCase() === 'adult' && (a.email === memail || a._isSelf));
      if (myAthletes.length === 0) {
        // No linked athletes at all — this user is buying for themselves.
        myAthletes = [selfStub];
      } else if ((userData.roles || []).includes('player') && !hasAdultSelf) {
        // Parent who is also a player: offer themselves alongside their kids.
        myAthletes.push(selfStub);
      }

      setAthletes(myAthletes.filter(a => a.athleteType !== 'unknown'));

      let autoSelectedAthlete = null;
      if (myAthletes.length === 1) {
        autoSelectedAthlete = myAthletes[0];
        setSelectedAthlete(autoSelectedAthlete);
      }

      setLoading(false);

      if (autoSelectedPlan && autoSelectedAthlete && !autoSelectedAthlete._isSelf) {
        // Same per-plan waiver gate as the manual checkout path. If the plan
        // has a waiverDocId set and the user hasn't accepted (or it expired),
        // open the dialog instead of auto-redirecting to Stripe.
        if (autoSelectedPlan.waiverDocId && auth.currentUser) {
          try {
            const wd = await getDoc(doc(db, 'clubDocuments', autoSelectedPlan.waiverDocId));
            if (wd.exists()) {
              const docRecord = { id: wd.id, ...wd.data() };
              setWaiverDoc(docRecord);
              const valid = await hasValidWaiverAcceptance(auth.currentUser.uid, docRecord);
              if (!valid) {
                setWaiverDialogOpen(true);
                return; // dialog accept will resume via startStripeCheckout
              }
            }
          } catch (e) {
            console.warn('Auto-checkout waiver gate failed; proceeding:', e);
          }
        }
        await startStripeCheckout(autoSelectedPlan, autoSelectedAthlete);
      }
    };
    fetchData();
  }, []);

  const handleCheckout = async () => {
    if (!selectedPlan || !selectedAthlete) { setError('Please select a plan and an athlete.'); return; }
    setError('');
    // If the selected athlete is the synthetic "_isSelf" placeholder, the buyer
    // has no real athlete doc yet. Rather than block the sale, create a real
    // adult athlete on the fly so checkout can proceed. (Junior plans still need
    // an actual child athlete, which we can't fabricate — prompt for that.)
    let athlete = selectedAthlete;
    if (athlete._isSelf) {
      const planType = (selectedPlan.athleteType || '').toLowerCase();
      if (planType === 'junior') {
        setError('This is a junior plan, but no junior athlete is linked to your account yet. Please add a junior athlete from your Profile page first, then return here to purchase.');
        return;
      }
      try {
        const muid = athlete._memberUid || auth.currentUser.uid;
        const athleteId = `ath_${muid}_${Date.now()}`;
        const newAthlete = {
          athleteId, parentUserId: null, userId: muid,
          firstName: athlete.firstName || '',
          lastName: athlete.lastName || '',
          email: athlete.email || '',
          phone: athlete.phone || '',
          athleteType: 'adult', skillGroup: 'beginner',
          sessionBalance: 0, weeklyBalance: 0,
          registrationDate: new Date(), createdAt: new Date(),
        };
        await setDoc(doc(db, 'athletes', athleteId), newAthlete);
        athlete = { id: athleteId, ...newAthlete };
        setSelectedAthlete(athlete);
        setAthletes(prev => prev.map(a => (a._isSelf ? athlete : a)));
      } catch (e) {
        logError('TrainingPlans', 'Auto-create athlete failed: ' + (e.message || e), e.stack || '');
        setError('Could not set up your athlete profile. Please try again.');
        return;
      }
    }
    // Per-plan waiver gate. Only fires if the plan has a waiverDocId set AND
    // we successfully loaded the doc. On any load failure we silently bypass
    // the gate (fail-open) so a missing/deleted doc never blocks a purchase.
    if (selectedPlan.waiverDocId) {
      try {
        let docRecord = waiverDoc;
        // Lazy-load if the doc cache for this plan isn't populated yet.
        if (!docRecord || docRecord.id !== selectedPlan.waiverDocId) {
          const wd = await getDoc(doc(db, 'clubDocuments', selectedPlan.waiverDocId));
          if (wd.exists()) {
            docRecord = { id: wd.id, ...wd.data() };
            setWaiverDoc(docRecord);
          }
        }
        if (docRecord && auth.currentUser) {
          const valid = await hasValidWaiverAcceptance(auth.currentUser.uid, docRecord);
          if (!valid) {
            setWaiverDialogOpen(true);
            return;
          }
        }
      } catch (e) {
        console.warn('Waiver gate check failed; proceeding without gate:', e);
      }
    }
    await startStripeCheckout(selectedPlan, athlete);
  };

  // Called by WaiverDialog when the user accepts. Writes the acceptance, then
  // resumes the Stripe checkout for the currently selected plan + athlete.
  // Throws on error so the dialog can surface it.
  async function handleAcceptWaiver(entry) {
    const user = auth.currentUser;
    if (!user || !waiverDoc || !selectedPlan || !selectedAthlete) {
      throw new Error('Session expired. Please refresh and try again.');
    }
    await updateDoc(doc(db, 'users', user.uid), {
      [`documentAcceptances.${waiverDoc.id}`]: entry,
    });
    setWaiverDialogOpen(false);
    await startStripeCheckout(selectedPlan, selectedAthlete);
  }

  // Extracted from handleCheckout so we can call it both pre-gate (no waiver
  // required, or already valid) and post-acceptance.
  async function startStripeCheckout(plan, athlete) {
    setCheckingOut(true);
    try {
      const functions = getFunctions();
      const isRecurring = plan.planType === 'recurring';
      const createSession = httpsCallable(functions, isRecurring ? 'createSubscriptionSession' : 'createCheckoutSession');
      const reserveParam = plan.promptReserve ? `&promptReserve=true&reserveClassType=${encodeURIComponent(plan.reserveClassType || '')}` : '';
      const successUrl = isRecurring
        ? `${window.location.origin}/payment-success?planId=${plan.id}&athleteId=${athlete.id}&sessions=${plan.sessionsIncluded}&amount=${plan.price}&planName=${encodeURIComponent(plan.planName)}&recurring=true&session_id={CHECKOUT_SESSION_ID}${reserveParam}`
        : `${window.location.origin}/payment-success?planId=${plan.id}&athleteId=${athlete.id}&sessions=${plan.sessionsIncluded}&amount=${plan.price}&planName=${encodeURIComponent(plan.planName)}&session_id={CHECKOUT_SESSION_ID}${reserveParam}`;
      const result = await createSession({ planId: plan.id, athleteId: athlete.id, successUrl, cancelUrl: `${window.location.origin}/training-plans` });
      window.location.href = result.data.url;
    } catch (err) {
      logError('TrainingPlans', err.message || String(err), err.stack || '');
      setError('Failed to start checkout. Please try again.');
      setCheckingOut(false);
    }
  }

  // In preview mode, force the athlete type filter to the previewed type.
  // Otherwise use the selected athlete's type.
  const athleteType = previewMode ? previewType : (selectedAthlete?.athleteType?.toLowerCase() || '');
  const filteredPlans = plans.filter(p => {
    if (!p.athleteType) return true;
    const pt = p.athleteType.toLowerCase();
    if (pt === 'both') return true;
    if (athleteType === 'junior') return pt === 'junior';
    if (athleteType === 'adult') return pt === 'adult';
    return true;
  });
  const bySortOrder = (a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999) || (a.price ?? 0) - (b.price ?? 0);
  const punchcardPlans = filteredPlans.filter(p => p.planType === 'punchcard').sort(bySortOrder);
  const recurringPlans = filteredPlans.filter(p => p.planType === 'recurring').sort(bySortOrder);
  const otherPlans = filteredPlans.filter(p => !['punchcard', 'recurring'].includes(p.planType)).sort(bySortOrder);

  const sectionLabel = (label) => (
    <Box sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: goldMid, mb: 1.5 }}>
      {label}
    </Box>
  );

  const PlanCard = ({ plan }) => {
    const isRecurring = plan.planType === 'recurring';
    const selected = selectedPlan?.id === plan.id;
    return (
      <Box onClick={() => setSelectedPlan(plan)} sx={{
        position: 'relative', background: selected ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.05)',
        border: selected ? `1.5px solid ${gold}` : `1px solid ${goldFaint}`,
        borderRadius: '12px', p: '20px', cursor: 'pointer', minWidth: 160, flex: '1 1 160px', maxWidth: 240,
        transition: 'all 0.2s',
        '&:hover': { borderColor: goldMid, background: 'rgba(201,168,76,0.08)' },
      }}>
        {selected && <CheckCircleOutlineIcon sx={{ position: 'absolute', top: 12, right: 12, color: gold, fontSize: '1.1rem' }} />}
        {isRecurring && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <AutorenewIcon sx={{ fontSize: 13, color: goldMid }} />
            <Box sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '1.5px', color: goldMid }}>MONTHLY</Box>
          </Box>
        )}
        <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.8rem', color: gold, lineHeight: 1, mb: 0.5 }}>
          ${plan.price}{isRecurring && <span style={{ fontSize: '1rem', color: goldMid }}>/mo</span>}
        </Box>
        <Box sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'white', mb: 1 }}>{plan.planName}</Box>
        {plan.description && <Box sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.4, mb: 1 }}>{plan.description}</Box>}
        {plan.sessionsIncluded && !isRecurring && (
          <Box sx={{ fontSize: '0.68rem', color: goldMid }}>
            {plan.sessionsIncluded} sessions · ${(plan.price / plan.sessionsIncluded).toFixed(2)}/session
          </Box>
        )}
        {isRecurring && <Box sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.3)', mt: 0.5 }}>Sessions roll over each month</Box>}
      </Box>
    );
  };

  const AthleteCard = ({ athlete }) => {
    const selected = selectedAthlete?.id === athlete.id;
    return (
      <Box onClick={() => setSelectedAthlete(athlete)} sx={{
        position: 'relative', background: selected ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.05)',
        border: selected ? `1.5px solid ${gold}` : `1px solid ${goldFaint}`,
        borderRadius: '12px', p: '16px 20px', cursor: 'pointer', minWidth: 160, flex: '1 1 160px', maxWidth: 220,
        transition: 'all 0.2s',
        '&:hover': { borderColor: goldMid, background: 'rgba(201,168,76,0.08)' },
      }}>
        {selected && <CheckCircleOutlineIcon sx={{ position: 'absolute', top: 10, right: 10, color: gold, fontSize: '1rem' }} />}
        <Box sx={{ fontSize: '0.88rem', fontWeight: 600, color: 'white' }}>{athlete.firstName} {athlete.lastName}</Box>
        <Box sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', mt: 0.5 }}>{athlete.sessionBalance || 0} sessions remaining</Box>
        {athlete.subscriptionStatus === 'active' && (
          <Box sx={{ mt: 0.5, fontSize: '0.62rem', color: goldMid, fontWeight: 600, letterSpacing: '0.5px' }}>● ACTIVE SUBSCRIPTION</Box>
        )}
      </Box>
    );
  };

  return (
    <Layout>
      <Box sx={{ background: dark, minHeight: '100%', p: 3, fontFamily: '"Barlow", sans-serif' }}>
        <Box sx={{ maxWidth: 860, margin: '0 auto' }}>

          {previewMode && (
            <Alert severity="warning" icon={false}
                   sx={{ mb: 3, bgcolor: '#C9A84C', color: '#0D1E35', border: '2px solid #fff', borderRadius: 2 }}>
              <Box sx={{ fontWeight: 800, letterSpacing: 1 }}>🔍 PREVIEW MODE — {previewType.toUpperCase()} PLANS</Box>
              <Box sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                This is what a {previewType} would see. Checkout is disabled. Real plans must have <code>athleteType</code> set to filter correctly.
              </Box>
            </Alert>
          )}

          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: 'clamp(1.8rem,3vw,2.4rem)', letterSpacing: '3px', color: 'white', lineHeight: 1 }}>
              Training Plans
            </Box>
            <Box sx={{ fontSize: '0.78rem', fontWeight: 300, color: 'rgba(255,255,255,0.35)', letterSpacing: '1px', mt: 0.5 }}>
              {athletes.length === 1 && selectedAthlete
                ? `Purchasing for ${selectedAthlete.firstName} ${selectedAthlete.lastName}`
                : 'Purchase a session plan.'}
            </Box>
            <Box sx={{ width: 50, height: 2, background: `linear-gradient(90deg, ${gold}, transparent)`, mt: 1.5 }} />
          </Box>

          {error && <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(211,47,47,0.15)', color: '#ff8a80', border: '1px solid rgba(211,47,47,0.3)', '& .MuiAlert-icon': { color: '#ff8a80' } }}>{error}</Alert>}

          {(loading || checkingOut) ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, gap: 2 }}>
              <CircularProgress sx={{ color: gold }} />
              <Box sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.35)' }}>
                {checkingOut ? 'Redirecting to secure checkout…' : 'Loading…'}
              </Box>
            </Box>
          ) : (
            <>
              {/* Step 1 - Select Athlete */}
              {athletes.length > 1 && (
                <Box sx={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${goldFaint}`, borderRadius: '14px', p: 3, mb: 3 }}>
                  <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1rem', letterSpacing: '2px', color: goldMid, mb: 2 }}>
                    Step 1 — Select Athlete
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {athletes.map(a => <AthleteCard key={a.id} athlete={a} />)}
                  </Box>
                </Box>
              )}

              {/* Step 2 - Select Plan */}
              {selectedAthlete && (
                <Box sx={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${goldFaint}`, borderRadius: '14px', p: 3, mb: 3 }}>
                  <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1rem', letterSpacing: '2px', color: goldMid, mb: 2.5 }}>
                    {athletes.length > 1 ? 'Step 2 — Select Plan' : 'Step 1 — Select Plan'}
                  </Box>
                  {punchcardPlans.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      {sectionLabel('Session Packs')}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {punchcardPlans.map(p => <PlanCard key={p.id} plan={p} />)}
                      </Box>
                    </Box>
                  )}
                  {recurringPlans.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      {sectionLabel('Monthly Subscriptions')}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {recurringPlans.map(p => <PlanCard key={p.id} plan={p} />)}
                      </Box>
                    </Box>
                  )}
                  {otherPlans.length > 0 && (
                    <Box>
                      {sectionLabel('Other Plans')}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {otherPlans.map(p => <PlanCard key={p.id} plan={p} />)}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              {/* Step 3 - Checkout */}
              {selectedPlan && selectedAthlete && (
                <Box sx={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${goldFaint}`, borderRadius: '14px', p: 3 }}>
                  <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1rem', letterSpacing: '2px', color: goldMid, mb: 2.5 }}>
                    {athletes.length > 1 ? 'Step 3 — Checkout' : 'Step 2 — Checkout'}
                  </Box>
                  <Box sx={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${goldFaint}`, borderRadius: '10px', p: 2.5, mb: 3 }}>
                    <Box sx={{ fontSize: '0.65rem', color: goldMid, letterSpacing: '1.5px', textTransform: 'uppercase', mb: 0.5 }}>Athlete</Box>
                    <Box sx={{ fontSize: '0.95rem', fontWeight: 600, color: 'white', mb: 1.5 }}>{selectedAthlete.firstName} {selectedAthlete.lastName}</Box>
                    <Box sx={{ fontSize: '0.65rem', color: goldMid, letterSpacing: '1.5px', textTransform: 'uppercase', mb: 0.5 }}>Plan</Box>
                    <Box sx={{ fontSize: '0.95rem', fontWeight: 600, color: 'white' }}>{selectedPlan.planName}</Box>
                    {selectedPlan.planType === 'recurring' && (
                      <Box sx={{ fontSize: '0.72rem', color: goldMid, mt: 0.5 }}>Billed monthly · Cancel anytime</Box>
                    )}
                    <Box sx={{ fontSize: '0.65rem', color: goldMid, letterSpacing: '1.5px', textTransform: 'uppercase', mt: 1.5, mb: 0.5 }}>
                      {selectedPlan.planType === 'recurring' ? 'Monthly Charge' : 'Total'}
                    </Box>
                    <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '2.2rem', color: gold, lineHeight: 1 }}>
                      ${selectedPlan.price}{selectedPlan.planType === 'recurring' && <span style={{ fontSize: '1.2rem', color: goldMid }}>/mo</span>}
                    </Box>
                  </Box>
                  <Box
                    onClick={(!checkingOut && !previewMode) ? handleCheckout : undefined}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                      background: (checkingOut || previewMode) ? 'rgba(201,168,76,0.4)' : gold,
                      color: dark, borderRadius: '8px', py: 1.8,
                      fontFamily: '"Barlow", sans-serif', fontSize: '0.95rem', fontWeight: 700,
                      letterSpacing: '1px', cursor: (checkingOut || previewMode) ? 'not-allowed' : 'pointer',
                      transition: 'background 0.2s',
                      '&:hover': { background: (checkingOut || previewMode) ? 'rgba(201,168,76,0.4)' : '#e0be6a' },
                    }}
                  >
                    {previewMode ? 'CHECKOUT DISABLED IN PREVIEW' :
                     checkingOut
                      ? <><CircularProgress size={16} sx={{ color: dark, mr: 1 }} />Redirecting to Stripe…</>
                      : selectedPlan.planType === 'recurring'
                        ? <><AutorenewIcon sx={{ fontSize: 18 }} />Subscribe ${selectedPlan.price}/month</>
                        : <><ShoppingCartIcon sx={{ fontSize: 18 }} />Pay ${selectedPlan.price} with Stripe</>
                    }
                  </Box>
                  <Box sx={{ textAlign: 'center', fontSize: '0.68rem', color: 'rgba(255,255,255,0.2)', mt: 1.5 }}>
                    Secure payment powered by Stripe.{selectedPlan.planType === 'recurring' ? ' Cancel anytime from your profile.' : ' Sessions added automatically after payment.'}
                  </Box>
                </Box>
              )}
            </>
          )}
          {/* Waiver gate dialog. Opens via handleCheckout or auto-checkout
              when the selected plan's waiverDocId points to a doc the buyer
              hasn't accepted (or that has expired). On accept: write to
              users/{uid}.documentAcceptances and resume Stripe checkout. */}
          <WaiverDialog
            open={waiverDialogOpen}
            doc={waiverDoc}
            onClose={() => { setWaiverDialogOpen(false); setCheckingOut(false); }}
            onAccepted={handleAcceptWaiver}
          />
        </Box>
      </Box>
    </Layout>
  );
}

export default TrainingPlans;
